import { useQuery } from '@tanstack/react-query';
import { studentService } from './studentService';
import { codingStudentService } from '@services/coding/codingStudentService';

/** Shape of `GET /students/me/dashboard` — loose on purpose; the API is untyped today. */
export interface StudentDashboardData {
  enrollment?: {
    number?: string;
    status?: string;
    attendance?: Array<{ id?: string; date?: string; status?: string }>;
    mentor?: { name?: string; email?: string; speciality?: string } | null;
  };
  cohort?: {
    id?: string;
    name?: string;
    title?: string;
    start_date?: string;
    duration?: number;
    status?: string;
    handbook_path?: string | null;
    is_cohort_1?: boolean;
    /** Added in the Phase 0 backend change. Absent ⇒ treat as "CLASSIC". */
    student_portal_version?: 'CLASSIC' | 'JOURNEY';
  };
  sidebar_modules?: Array<{
    id?: string;
    title?: string;
    slug?: string;
    description?: string | null;
    icon?: string;
    route?: string;
    order_no?: number;
    is_cohort_specific?: boolean;
  }>;
  progress?: { days_passed?: number; total_days?: number; percentage?: number };
  mentor?: { name?: string; email?: string; speciality?: string } | null;
  profile_completion?: {
    percentage: number;
    is_complete: boolean;
    sections?: {
      basic_details: boolean;
      school_contact: boolean;
      academic_profile: boolean;
      profile_picture: boolean;
      project_upload?: boolean;
    };
  };
}

/** `GET /students/me/schedule` — array of weeks with date-enriched days. */
export interface ScheduleWeek {
  week_number?: number;
  title?: string | null;
  is_active?: boolean;
  days?: Array<{
    day_number?: number;
    date?: string;
    title?: string;
    description?: string | null;
    is_active?: boolean;
    is_unlocked?: boolean;
    is_link_active?: boolean;
    is_mentor_session?: boolean;
    attendance_status?: string | null;
    link?: string | null;
    recorded_video?: string | null;
    recorded_video_path?: string | null;
    recorded_video_name?: string | null;
    recordings?: Array<{ path?: string; name?: string }>;
    student_links?: Array<{ enrollment_id?: string; link?: string; meeting_time?: string }>;
  }>;
}

/** `GET /students/me/documents`. */
export interface StudentDocuments {
  lor_path?: string | null;
  certificate?: { certificate_url?: string } | null;
}

/** `GET /students/me/reading-material/:week`. */
export interface WeekReadingMaterial {
  week_number?: number;
  title?: string;
  content?: string; // markdown
}

export const STUDENT_DASHBOARD_KEY = ['student', 'dashboard'] as const;
export const STUDENT_SCHEDULE_KEY = ['student', 'schedule'] as const;
export const STUDENT_DOCUMENTS_KEY = ['student', 'documents'] as const;
/**
 * Journey-only cache key. Deliberately NOT the CLASSIC pages'
 * `['student-coding-problems']`: those store the *raw axios response*
 * (`{ data: { data: [...] } }`), this hook stores a plain
 * `StudentCodingProblemListItem[]`. Sharing one key let the two shapes collide
 * — e.g. opening `/student/lab` (StudentNotebook) then a Journey coding page
 * fed `deriveJourney` an object, crashing with "problems is not iterable".
 */
export const STUDENT_CODING_PROBLEMS_KEY = ['journey', 'coding-problems'] as const;
export const weekReadingKey = (week: number) => ['student', 'week-reading', week] as const;

/**
 * Canonical Student Portal dashboard query. Shared by the layout gate, the
 * JourneyShell, and Journey pages so they hit the cache, not the network,
 * repeatedly. (The CLASSIC `StudentLayoutInner` keeps its own raw fetch — left
 * untouched — so Cohort 1 behaviour is unchanged.)
 */
export function useStudentDashboard(enabled = true) {
  return useQuery({
    queryKey: STUDENT_DASHBOARD_KEY,
    queryFn: async (): Promise<StudentDashboardData> => {
      const res = await studentService.getDashboard();
      return res.data.data as StudentDashboardData;
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useStudentSchedule(enabled = true) {
  return useQuery({
    queryKey: STUDENT_SCHEDULE_KEY,
    queryFn: async (): Promise<ScheduleWeek[]> => {
      const res = await studentService.getSchedule();
      return (res.data.data ?? []) as ScheduleWeek[];
    },
    enabled,
    staleTime: 60_000,
  });
}

export function useStudentDocuments(enabled = true) {
  return useQuery({
    queryKey: STUDENT_DOCUMENTS_KEY,
    queryFn: async (): Promise<StudentDocuments> => {
      const res = await studentService.getDocuments();
      return (res.data.data ?? {}) as StudentDocuments;
    },
    enabled,
    staleTime: 60_000,
  });
}

/** Always resolves to a plain `StudentCodingProblemListItem[]` (see key note above). */
export function useStudentCodingProblems(enabled = true) {
  return useQuery({
    queryKey: [...STUDENT_CODING_PROBLEMS_KEY],
    queryFn: async () => {
      const res = await codingStudentService.getStudentProblems();
      return res.data?.data ?? [];
    },
    enabled,
    staleTime: 60_000,
  });
}

export const codingProblemKey = (idOrSlug: string) => ['student-coding-problem', idOrSlug] as const;

export function useCodingProblemDetails(idOrSlug: string | null | undefined, enabled = true) {
  return useQuery({
    queryKey: codingProblemKey(idOrSlug ?? ''),
    queryFn: async () => {
      const res = await codingStudentService.getProblemDetails(idOrSlug!);
      return res.data.data;
    },
    enabled: enabled && !!idOrSlug,
    staleTime: 30_000,
  });
}

export function useWeekReadingMaterial(week: number, enabled = true) {
  return useQuery({
    queryKey: weekReadingKey(week),
    queryFn: async (): Promise<WeekReadingMaterial | null> => {
      try {
        const res = await studentService.getWeekReadingMaterial(week);
        return (res.data?.data ?? null) as WeekReadingMaterial | null;
      } catch (e: unknown) {
        // 404 ⇒ this week simply has no reading material yet.
        if ((e as { response?: { status?: number } })?.response?.status === 404) return null;
        throw e;
      }
    },
    enabled: enabled && Number.isFinite(week),
    staleTime: 5 * 60_000,
    retry: false,
  });
}
